import { useEffect, useState } from 'react';
import Select from 'react-select';
import Spinner from '../../reusable/spinner/spinner.component';

import Client from '../../../tools/client';
import {
  FieldSection,
  FieldGroup,
  Label,
  Toggle,
  Note,
  Button
} from '../../configuration/sections/configuration-sections.styles';

const client = new Client();

const IntegrationTypeDisplay = ({ integrationType, onUpdate = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);

  const integrationTypeName = integrationType?.name;

  useEffect(() => {
    if (!integrationType) return;

    const flatConfig = {};
    for (const [key, value] of Object.entries(integrationType.configuration || {})) {
      flatConfig[key] = value;
    }

    const validIntegrations = (integrationType.integrations || []).filter(isIntegrationComplete);

    const providerKey = `integrations.${integrationTypeName}.provider.id`;
    const enabledKey = `integrations.${integrationTypeName}.enabled`;

    if (!flatConfig[providerKey] && validIntegrations.length > 0) {
      flatConfig[providerKey] = validIntegrations[0].id;
    }

    if (typeof flatConfig[enabledKey] !== 'boolean') {
      flatConfig[enabledKey] = !!integrationType.configuration?.enabled;
    }

    setForm(flatConfig);
  }, [integrationType]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const providerKey = `integrations.${integrationTypeName}.provider.id`;
    const enabledKey = `integrations.${integrationTypeName}.enabled`;
    const providerId = form[providerKey];

    if (form[enabledKey] && !providerId) {
      alert('Please select a provider before enabling this integration.');
      return;
    }

    try {
      setLoading(true);
      await client.updateIntegration({
        integrationId: providerId,
        configuration: {
          [providerKey]: providerId,
          [enabledKey]: !!form[enabledKey],
        }
      });

      setEditing(false);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to save integration type config:', err);
    } finally {
      setLoading(false);
    }
  };

  const isIntegrationComplete = (integration) => {
    if (!integration.keySchemas?.length) return true;
    const secrets = integration.secrets || [];
    return integration.keySchemas.every(schema => {
      if (!schema.required) return true;
      const match = secrets.find(s => s.keyName === schema.keyName);
      return !!match?.value && match.value.length > 0;
    });
  };

  if (!integrationType || loading) return <Spinner />;

  const validIntegrations = (integrationType.integrations || []).filter(isIntegrationComplete);

  const providerKey = `integrations.${integrationTypeName}.provider.id`;
  const enabledKey = `integrations.${integrationTypeName}.enabled`;

  const providerOptions = validIntegrations.map((i) => ({
    value: i.id,
    label: i.name
  }));

  // Current values
  const currentProviderId = integrationType.configuration?.provider?.id;
  const isCurrentlyEnabled = !!integrationType.configuration?.enabled;
  const activeProvider = validIntegrations.find(i => i.id === currentProviderId);

  // Update values
  const selectedProviderId = form[providerKey];
  const selectedOption = providerOptions.find(opt => opt.value === selectedProviderId);

  return (
    <FieldSection>
      <FieldGroup>
        <Label>{integrationType.displayName || `Configure ${integrationType.name}`}</Label>
        <Toggle>
          <input
            type="checkbox"
            checked={!!form[enabledKey]}
            onChange={() => handleChange(enabledKey, !form[enabledKey])}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      <FieldGroup>
        <Label>Select Provider</Label>
        <Select
          value={selectedOption}
          onChange={(opt) => handleChange(providerKey, opt.value)}
          options={providerOptions}
          isDisabled={!editing}
        />
        {form[providerKey] && !selectedOption && (
          <Note style={{ marginTop: '0.5rem', color: 'orange' }}>
            Selected provider is missing required secrets. Please select another provider.
          </Note>
        )}
        {activeProvider && (
          <Note style={{ marginTop: '0.5rem' }}>
            {isCurrentlyEnabled ? (
              <span style={{ color: 'green' }}>
                Active provider: <strong>{activeProvider.name}</strong>
              </span>
            ) : (
              <span>
                Selected provider: <strong>{activeProvider.name}</strong> (inactive)
              </span>
            )}
          </Note>
        )}
      </FieldGroup>

      <Button
        disabled={loading}
        onClick={() => setEditing(!editing)}
        style={{ marginRight: '1rem' }}
      >
        {editing ? 'Cancel' : 'Edit'}
      </Button>
      {editing && (
        <Button primary disabled={loading} onClick={handleSave}>
          Save
        </Button>
      )}
    </FieldSection>
  );
};

export default IntegrationTypeDisplay;